import React from "react";
import { Box, Typography, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { StockComparisonSnapshot } from "../../../types/StockComparison";
import { useCompanyProfile } from "../Charts/hooks/useCompanyProfile";
import { formatStockPrice } from "../../../utils/stockPriceFormatter";
import { getSymbolImageUrl } from "../../../lib/symbolImage";

interface ComparisonStockPanelProps {
  snapshot: StockComparisonSnapshot;
  color: string;
  onClear: () => void;
}

const ComparisonStockPanel = ({ snapshot, onClear }: ComparisonStockPanelProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { data: profileData } = useCompanyProfile(snapshot.ticker);
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;

  // Exact same values as ViewTicker's getFlatUIColors
  const bg = isDark ? "#1a1a1a" : "#ffffff";
  const surface = isDark ? "#212121" : "#f8fafc";
  const border = isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0";
  const textPrimary = isDark ? "#e4e6ea" : "#0f172a";
  const textSecondary = isDark ? "#9ca3af" : "#64748b";
  const textMuted = isDark ? "#6b7280" : "#94a3b8";

  const companyName = profile?.companyName ?? snapshot.name ?? snapshot.ticker;
  const exchange = profile?.exchange ?? null;
  const currency = profile?.currency;

  const isPositive = (snapshot.changePercentage ?? 0) >= 0;
  const changeColor = isPositive ? "#22c55e" : "#ef4444";

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        px: { xs: 2, lg: 3 },
        gap: { xs: 1.5, lg: 2 },
        backgroundColor: bg,
      }}
    >
      {/* Logo — 40px desktop, 32px mobile */}
      <Box
        sx={{
          position: "relative",
          width: { xs: 32, lg: 40 },
          height: { xs: 32, lg: 40 },
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={getSymbolImageUrl(snapshot.ticker)}
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          alt={`${snapshot.ticker} logo`}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = "flex";
          }}
        />
        <Box
          sx={{
            display: "none",
            position: "absolute",
            top: 0,
            left: 0,
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            borderRadius: 1,
            backgroundColor: isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)",
            border: isDark ? "1px solid rgba(34,197,94,0.3)" : "1px solid rgba(34,197,94,0.2)",
            fontSize: { xs: "0.875rem", lg: "1.25rem" },
            fontWeight: 700,
            color: "#22c55e",
          }}
        >
          {companyName.charAt(0).toUpperCase()}
        </Box>
      </Box>

      {/* Company name → ticker → exchange badge — exact ViewTicker layout */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, overflow: "hidden", py: { xs: 1, sm: 0 } }}>
        <Typography noWrap sx={{ fontSize: "1rem", fontWeight: 600, color: textPrimary, lineHeight: 1 }}>
          {companyName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.875rem", color: textSecondary, fontWeight: 500 }}>
            {snapshot.ticker}
          </Typography>
          {exchange && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                backgroundColor: surface,
                border: `1px solid ${border}`,
                borderRadius: 0,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  color: textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {exchange}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Vertical divider */}
      <Box
        sx={{
          width: "1px",
          height: "40px",
          backgroundColor: border,
          mx: 1,
          display: { xs: "none", lg: "block" },
          flexShrink: 0,
        }}
      />

      {/* Price + change — desktop */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: textPrimary }}>
          {snapshot.price != null ? formatStockPrice({ price: snapshot.price, currency }) : "—"}
        </Typography>
        {snapshot.changePercentage != null && snapshot.change != null && (
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: changeColor }}>
            {isPositive ? "+" : "-"}{formatStockPrice({ price: Math.abs(snapshot.change), currency })} ({isPositive ? "+" : ""}
            {snapshot.changePercentage.toFixed(2)}%)
          </Typography>
        )}
      </Box>

      {/* Price + change — mobile compact */}
      <Box
        sx={{
          display: { xs: "flex", lg: "none" },
          flexDirection: "column",
          alignItems: "flex-end",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{ fontSize: "0.9375rem", fontWeight: 700, color: textPrimary, lineHeight: 1.2 }}
        >
          {snapshot.price != null ? formatStockPrice({ price: snapshot.price, currency }) : "—"}
        </Typography>
        {snapshot.changePercentage != null && (
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 600, color: changeColor, lineHeight: 1.2 }}
          >
            {isPositive ? "+" : ""}
            {snapshot.changePercentage.toFixed(2)}%
          </Typography>
        )}
      </Box>

      {/* Close button */}
      <IconButton
        size="small"
        onClick={onClear}
        sx={{
          color: textMuted,
          flexShrink: 0,
          "&:hover": {
            color: textPrimary,
            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          },
        }}
        aria-label="Remove stock"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default ComparisonStockPanel;
